import React, {useCallback, useEffect, useState} from "react";
import {makeStyles} from "@material-ui/core";
import {Loading} from "@cabinet-packages/components";
import {useSelector} from "react-redux";
import Typography from "@material-ui/core/Typography";
import CabinetAutocomplete from "../components/Cabinet/CabinetAutocomplete";
import {
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from "chart.js";
import ChartAnnotationsPlugin from "chartjs-plugin-annotation";
import AssayAccordion from "../components/Cabinet/Accordions/AssayAccordion";
import {useHistory} from "react-router-dom";
import {SwipeButton} from "../components/Cabinet/SwipeStyledButton";
import {getHistoryByClientId} from "@cabinet-packages/api/api";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ChartAnnotationsPlugin
);

const useStyles = makeStyles((theme) => ({
    scrollContainer: {
        padding: '0px 5px 0 0',
        height: 'max-content', // Set a fixed height
        overflowY: 'scroll', // Enable scrolling
        width: '100%',

        '@media(min-width: 900px)': {
            '&::-webkit-scrollbar': {
                display: 'none'
            }
        }
    },
    rootWrapper: {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'column',
        rowGap: '8px',
        padding: '10px',
        margin: '0 auto',
        height: "max-content",
        width: '100%',
        '@media (max-width:1200px)': {
            justifyItems: 'center',
            gridTemplateColumns: '1fr 1fr',
        },
        '@media (max-width:600px)': {
            gridTemplateColumns: '1fr',
        },
    },
    assaysWrapper: {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'column',
        rowGap: '8px',
        padding: '10px',
        width: '100%',
        margin: '0 auto',
        height: "max-content",

        '@media (max-width:1200px)': {
            justifyItems: 'center',
            gridTemplateColumns: '1fr 1fr',
            width: '100%',
        },
        '@media (max-width:600px)': {
            gridTemplateColumns: '1fr',
        },
    },
    searchHeader: {
        margin: "8px 0px 12px 0px",
    },
    headerLabel: {
        color: theme.palette.primary.dark,
    },
    extraLabel: {
        color: theme.palette.primary.main,
    },
    assaysContainer: {
        height: "max-content",
        display: "flex",
        flexDirection: "column",
        rowGap: "7px",
    },
    noAvailableData: {
        textAlign: "center",
        margin: "auto",
        marginTop: "24px",
        "@media(max-width: 900px)": {
            marginTop: "16px",
        },
    },
    columnLabelsContainer: {
        display: "flex",
        justifyContent: "space-between",
        padding: "0px",
        marginBottom: "10px",
    },
    columnLabel: {
        fontSize: "12px",
        color: theme.palette.grey.text,
        fontWeight: 500,
        "@media(max-width: 900px)": {
            fontSize: "9px",
        },
    },
}));

export default function CabinetPatientAssaysHistory({activePatient}) {
    const classes = useStyles();
    const history = useHistory();
    const [assays, setAssays] = useState(null);
    const [filterValue, setFilterValue] = useState("");
    const [filteredState, setFilteredState] = useState(assays);
    const [prevStateOfCatalog, setPrevStateOfCatalog] = useState();
    const [expanded, setExpanded] = useState(false);
    const {catalog, complexResearch} = useSelector(state => state.assaysCatalog);

    const handleChangeFilterValue = (event) => {
        const newValue = event.target.value;

        setFilterValue(() => {
            if (newValue && newValue.trim().length === 0) {
                setFilteredState(assays);
            } else {
                setFilteredState(
                    assays &&
                    assays.filter((item) =>
                        compareFunc(item, newValue && newValue.trim().toLowerCase())
                    )
                );
            }
            return newValue;
        });
    };

    function compareFunc(item, value) {
        if (
            (item.name &&
                item.name.toLowerCase().includes(value.trim().toLowerCase())) ||
            (item.testNames && item.testNames.includes(value.trim()))
        ) {
            return true;
        } else
            return (
                item.filling &&
                !!item.filling.some(
                    (element) =>
                        element.test &&
                        element.test.toLowerCase().includes(value.trim().toLowerCase())
                )
            );
    }

    const handleAssayHistory = useCallback(async () => {
        if (catalog && complexResearch) {
            setAssays(null);

            // const assayHistory = await getOrdersResult(activePatient.id).catch(() => 'error')
            const assayHistory = await getHistoryByClientId(activePatient.id).catch(
                () => "error"
            );

            if (assayHistory) {
                const filteredAssays = assayHistory?.filter(
                    (item) => item?.history?.length > 1
                );

                setAssays(filteredAssays ? filteredAssays : []);
                setFilteredState(filteredAssays);
            }
        } else {
            setAssays([]);
            setFilteredState([]);
        }
    }, [activePatient, catalog, complexResearch])

    useEffect(() => {
        handleAssayHistory();
    }, [handleAssayHistory]);

    return (
        <div className={classes.scrollContainer}>
            <div>
            </div>
            {!assays ? (
                <div className={classes.noAvailableData}>
                    Завантаження історії досліджень, будь ласка зачекайте.
                    <br/>
                    <br/>
                    <Loading/>
                </div>
            ) : (
                <>
                    {assays && assays.length !== 0
                        ? (<div className={classes.assaysWrapper}>
                            <div className={classes.searchHeader}>
                                <CabinetAutocomplete
                                    useCatalogForView={[assays, setAssays]}
                                    handleValue={[filterValue, handleChangeFilterValue]}
                                    usePrevState={[prevStateOfCatalog, setPrevStateOfCatalog]}
                                />
                            </div>
                            <div className={classes.columnLabelsContainer}>
                                <Typography className={classes.columnLabel}>
                                    Назва дослідження
                                </Typography>
                                <Typography className={classes.columnLabel}></Typography>
                            </div>
                            {filteredState && filteredState.length !== 0 ? (
                                <div className={classes.assaysContainer}>
                                    {filteredState.map((item) => (
                                        <AssayAccordion
                                            key={JSON.stringify(item)}
                                            assay={item}
                                            expanded={expanded}
                                            setExpanded={setExpanded}
                                            catalog={catalog}
                                            complexResearch={complexResearch}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className={classes.noAvailableData}>
                                    <Typography style={{fontWeight: 500}}>
                                        Результатів за пошуком не знайдено.
                                    </Typography>
                                </div>
                            )}
                        </div>)
                        : assays !== "error" ? (
                            <div className={classes.noAvailableData}>
                                <Typography>
                                    Статистика досліджень відсутня. <br/> Бажаєте замовити
                                    дослідження ?
                                </Typography>
                                <SwipeButton
                                    onClick={() => history.push("/catalog-table")}
                                    text={"Замовити"}
                                />
                            </div>
                        ) : (
                            <div className={classes.noAvailableData}>
                                Вибачте, сталась помилка під час завантаження.
                            </div>
                        )}
                </>
            )}
        </div>
    );
}
